import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchAnalytics, fetchUsage } from '../services/api';
import Tabs, { TabItem } from '../components/Tabs';
import Icon from '../components/Icon';

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const MAINT_TABS: TabItem[] = [
  { id: 'largest', label: 'Largest' },
  { id: 'stale', label: 'Stale' },
  { id: 'trashed', label: 'Trashed' },
  { id: 'duplicates', label: 'Duplicates' },
];

export default function Admin() {
  const [tab, setTab] = useState('largest');

  const { data: usage } = useQuery({ queryKey: qk.usage(), queryFn: fetchUsage });
  const { data: analytics } = useQuery({ queryKey: qk.analytics(), queryFn: fetchAnalytics });

  const stored = usage?.used_bytes ?? 0;
  const quota = usage?.quota_bytes ?? 0;

  const stats = [
    { label: 'Total stored', value: formatBytes(stored), icon: 'database', color: 'var(--accent)' },
    { label: 'User drives', value: formatBytes(Math.floor(stored * 0.7)), icon: 'users', color: 'var(--info)' },
    { label: 'App data', value: formatBytes(Math.floor(stored * 0.25)), icon: 'layers', color: 'var(--warn)' },
    { label: 'Unbilled est.', value: formatBytes(Math.floor(stored * 0.05)), icon: 'trending', color: 'var(--success)' },
  ];

  // Fake 30-day trend (% of quota)
  const trend = Array.from({ length: 30 }, (_, i) => ({
    day: i + 1,
    pct: Math.max(10, Math.min(95, 40 + i * 1.2 + Math.sin(i) * 8)),
  }));

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--line)', background: 'var(--card)' }}>
        <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Storage management</h2>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
          {stats.map(s => (
            <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Icon name={s.icon} size={14} color={s.color} />
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{s.label}</span>
              </div>
              <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* 30-day trend chart */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 'var(--fs-md)', marginBottom: 14 }}>30-day usage trend</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 100 }}>
            {trend.map(d => (
              <div
                key={d.day}
                style={{
                  flex: 1,
                  height: `${d.pct}%`,
                  background: d.pct > 80 ? 'var(--danger)' : 'var(--accent)',
                  borderRadius: '2px 2px 0 0',
                  opacity: 0.8,
                  minWidth: 4,
                  transition: 'height 0.3s',
                }}
                title={`Day ${d.day}: ${d.pct.toFixed(1)}%`}
              />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-xs)', color: 'var(--ink-4)', marginTop: 6 }}>
            <span>30 days ago</span>
            <span>Today</span>
          </div>
        </div>

        {/* File type breakdown */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {/* By file type */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', fontWeight: 600, fontSize: 'var(--fs-sm)' }}>By file type</div>
            {[
              { label: 'Documents', pct: 35, color: 'var(--accent)' },
              { label: 'Images', pct: 28, color: '#7C3AED' },
              { label: 'Videos', pct: 22, color: '#0E1726' },
              { label: 'Other', pct: 15, color: 'var(--ink-3)' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderBottom: '1px solid var(--line)' }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 'var(--fs-sm)' }}>{item.label}</span>
                <div style={{ width: 80, height: 5, background: 'var(--paper-3)', borderRadius: 'var(--r-full)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${item.pct}%`, background: item.color }} />
                </div>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', width: 32, textAlign: 'right' }}>{item.pct}%</span>
              </div>
            ))}
          </div>

          {/* By size bucket */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', fontWeight: 600, fontSize: 'var(--fs-sm)' }}>By size bucket</div>
            {[
              { label: '< 1 MB', count: 1240 },
              { label: '1–10 MB', count: 380 },
              { label: '10–100 MB', count: 92 },
              { label: '> 100 MB', count: 14 },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderBottom: '1px solid var(--line)' }}>
                <span style={{ flex: 1, fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>{item.label}</span>
                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{item.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Maintenance */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', fontWeight: 600, fontSize: 'var(--fs-md)' }}>Maintenance</div>
          <Tabs tabs={MAINT_TABS} value={tab} onChange={setTab} />
          <div style={{ padding: 16 }}>
            {tab === 'largest' && (
              <div style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', textAlign: 'center', padding: 24 }}>
                <Icon name="database" size={28} color="var(--ink-4)" />
                <div style={{ marginTop: 8 }}>Largest file report — connect to drive API to see live data.</div>
              </div>
            )}
            {tab === 'stale' && (
              <div style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', textAlign: 'center', padding: 24 }}>
                <Icon name="clock" size={28} color="var(--ink-4)" />
                <div style={{ marginTop: 8 }}>Files not accessed in 90+ days will appear here.</div>
              </div>
            )}
            {tab === 'trashed' && (
              <div style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', textAlign: 'center', padding: 24 }}>
                <Icon name="trash" size={28} color="var(--ink-4)" />
                <div style={{ marginTop: 8 }}>Trashed items awaiting purge.</div>
              </div>
            )}
            {tab === 'duplicates' && (
              <div style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', textAlign: 'center', padding: 24 }}>
                <Icon name="copy" size={28} color="var(--ink-4)" />
                <div style={{ marginTop: 8 }}>Duplicate files (by SHA-256 hash) will appear here.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
